export interface ImportedProfile {
  anrede: string;
  vorname: string;
  nachname: string;
  email: string;
  telefon: string;
  mobiltelefon: string;
  extractedText: string;
  position?: string;
  skills?: string;
  erfahrung?: string;
  ausbildung?: string;
  sprachen?: string;
  standort?: string;
  verfuegbarkeit?: string;
  gehaltsvorstellung?: string;
  notizen?: string;
}

const FIRST_NAME_KEYS = ['vorname', 'firstname', 'first name', 'first_name', 'prénom', 'prenom', 'nome'];
const LAST_NAME_KEYS = ['nachname', 'lastname', 'last name', 'last_name', 'surname', 'familienname', 'cognome'];
const FULL_NAME_KEYS = ['name', 'vollständiger name', 'vollstaendiger name', 'full name', 'fullname', 'vor- und nachname'];
const EMAIL_KEYS = ['email', 'e-mail', 'e mail', 'mail', 'emailadresse', 'e-mail-adresse', 'emailaddress'];
const PHONE_KEYS = ['telefon', 'phone', 'tel', 'telefonnummer', 'festnetz', 'telephone'];
const MOBILE_KEYS = ['mobiltelefon', 'mobil', 'mobile', 'handy', 'mobilnummer', 'cell', 'cellphone'];
const SALUTATION_KEYS = ['anrede', 'salutation'];

// Matching needs more than master data. These are the columns the agents actually
// score against, so they are mapped into structured fields as well as extractedText.
const POSITION_KEYS = ['position', 'stelle', 'jobtitel', 'job title', 'berufsbezeichnung', 'aktuelle position', 'desired position', 'wunschposition'];
const SKILL_KEYS = ['skills', 'kenntnisse', 'faehigkeiten', 'fähigkeiten', 'qualifikationen', 'kompetenzen', 'technologien'];
const EXPERIENCE_KEYS = ['erfahrung', 'berufserfahrung', 'experience', 'work experience', 'jahre erfahrung', 'years of experience'];
const EDUCATION_KEYS = ['ausbildung', 'bildung', 'education', 'abschluss', 'studium', 'degree'];
const LANGUAGE_KEYS = ['sprachen', 'languages', 'sprachkenntnisse'];
const LOCATION_KEYS = ['standort', 'ort', 'stadt', 'location', 'city', 'wohnort'];
const AVAILABILITY_KEYS = ['verfügbarkeit', 'verfuegbarkeit', 'availability', 'verfügbar ab', 'verfuegbar ab', 'eintrittstermin', 'startdatum'];
const SALARY_KEYS = ['gehaltsvorstellung', 'gehalt', 'salary', 'salary expectation', 'wunschgehalt'];
const NOTES_KEYS = ['notizen', 'notes', 'interview', 'interviewnotizen', 'interview notes', 'bemerkungen', 'kommentar', 'anmerkungen'];

function valueAsString(value: any): string {
  if (value === undefined || value === null) {
    return '';
  }
  return String(value).trim();
}

/**
 * Dumps every non-empty column of a row as "Header: value" lines. This becomes
 * the extractedText so the AI enrichment can recover any field our deterministic
 * mapping missed (non-template files with arbitrary column names).
 */
export function buildRowExtractedText(row: any): string {
  if (!row) {
    return '';
  }
  const lines: string[] = [];
  Object.keys(row).forEach((key) => {
    const value = valueAsString(row[key]);
    if (value !== '') {
      lines.push(`${String(key).trim()}: ${value}`);
    }
  });
  return lines.join('\n');
}

export function mapRowToProfile(row: any): ImportedProfile {
  const norm: { [key: string]: any } = {};
  Object.keys(row || {}).forEach((key) => {
    norm[String(key).trim().toLowerCase()] = row[key];
  });
  const pick = (keys: string[]): string => {
    for (const key of keys) {
      const value = valueAsString(norm[key]);
      if (value !== '') {
        return value;
      }
    }
    return '';
  };
  let vorname = pick(FIRST_NAME_KEYS);
  let nachname = pick(LAST_NAME_KEYS);
  const fullName = pick(FULL_NAME_KEYS);
  if (!vorname && !nachname && fullName) {
    const parts = fullName.split(/\s+/);
    vorname = parts[0] || '';
    nachname = parts.slice(1).join(' ');
  }
  return {
    anrede: pick(SALUTATION_KEYS),
    vorname: vorname,
    nachname: nachname,
    email: pick(EMAIL_KEYS),
    telefon: pick(PHONE_KEYS),
    mobiltelefon: pick(MOBILE_KEYS),
    position: pick(POSITION_KEYS),
    skills: pick(SKILL_KEYS),
    erfahrung: pick(EXPERIENCE_KEYS),
    ausbildung: pick(EDUCATION_KEYS),
    sprachen: pick(LANGUAGE_KEYS),
    standort: pick(LOCATION_KEYS),
    verfuegbarkeit: pick(AVAILABILITY_KEYS),
    gehaltsvorstellung: pick(SALARY_KEYS),
    notizen: pick(NOTES_KEYS),
    extractedText: buildRowExtractedText(row)
  };
}

export function hasUsableProfileData(profile: ImportedProfile): boolean {
  return !!(profile && (profile.email || profile.vorname || profile.nachname));
}

export function buildExtractedTextFallback(fullName: string, email: string, phone: string, mobil: string): string {
  const parts: string[] = [];
  if (fullName) {
    parts.push(fullName);
  }
  if (email) {
    parts.push('E-Mail: ' + email);
  }
  if (phone) {
    parts.push('Telefon: ' + phone);
  }
  if (mobil) {
    parts.push('Mobil: ' + mobil);
  }
  return parts.join('\n');
}

/**
 * FirestoreTriggerEmailApplicantWithCV only parses the sent extractedText and keeps the
 * sent master data for quelle values on its easyQuellen list. Any other quelle takes the
 * e-mail path, which rebuilds the applicant from attachments alone and drops every
 * imported field. The human-readable origin is kept in importQuelle instead.
 */
export const ENRICHMENT_QUELLE = 'externalAPI';
export const DEFAULT_IMPORT_QUELLE = 'Bewerber-Import';

/**
 * Builds the document pushed to the emailapplicants collection. The CV
 * enrichment trigger parses extractedText and fills the structured fields the
 * agents rely on, so extractedText is guaranteed to be non-empty.
 */
export function buildApplicantImportDoc(profil: any): any {
  const nowIso = new Date().toISOString();
  const firstName = valueAsString(profil.vorname) || valueAsString(profil.firstName);
  const lastName = valueAsString(profil.nachname) || valueAsString(profil.lastName);
  const fullName = (valueAsString(profil.name) || `${firstName} ${lastName}`).trim();
  const mobil = valueAsString(profil.mobiltelefon) || valueAsString(profil.mobilnummer);
  const phone = valueAsString(profil.telefon) || valueAsString(profil.telefonnummer) || valueAsString(profil.phoneNumber);
  const extractedText = valueAsString(profil.extractedText) || valueAsString(profil.rawText)
    || buildExtractedTextFallback(fullName, valueAsString(profil.email), phone, mobil);
  return {
    vorname: firstName,
    nachname: lastName,
    firstName: firstName,
    lastName: lastName,
    name: fullName,
    email: valueAsString(profil.email),
    phoneNumber: valueAsString(profil.phoneNumber) || phone,
    telefonnummer: phone,
    telefon: phone,
    mobilnummer: mobil,
    mobiltelefon: mobil,
    extractedText: extractedText,
    position: valueAsString(profil.position),
    skills: valueAsString(profil.skills),
    erfahrung: valueAsString(profil.erfahrung),
    ausbildung: valueAsString(profil.ausbildung),
    sprachen: valueAsString(profil.sprachen),
    standort: valueAsString(profil.standort),
    verfuegbarkeit: valueAsString(profil.verfuegbarkeit),
    gehaltsvorstellung: valueAsString(profil.gehaltsvorstellung),
    notizen: valueAsString(profil.notizen),
    files: Array.isArray(profil.files) ? profil.files : [],
    documents: Array.isArray(profil.documents) ? profil.documents : [],
    cv: valueAsString(profil.cv),
    quelle: ENRICHMENT_QUELLE,
    importQuelle: valueAsString(profil.quelle) || DEFAULT_IMPORT_QUELLE,
    status: valueAsString(profil.status) || 'Neu',
    createdISO: nowIso,
    createdGerman: new Date().toLocaleString('de-DE')
  };
}
